/**
 * Сервис для работы с API действий (Actions Admin)
 */

import authService from './auth.service';
import type {
  ActionGroup,
  Action,
  CreateActionGroupPayload,
  UpdateActionGroupPayload,
  CreateActionPayload,
  UpdateActionPayload,
  ActionGroupListParams,
  ActionListParams,
  BulkUpdateGroupPayload,
  BulkUpdateServiceTypesPayload,
  BulkOperationResponse,
  AddServiceTypePayload,
  ActionStage,
  ActionStagesResponse,
  AddActionStagePayload,
  UpdateActionStagePayload,
  SetActionStagesPayload,
  SetActionStagesResponse,
  ActionsTreeResponse,
  ActionsTreeParams,
} from '../types/action-admin.types';

const API_GROUPS = '/api/v1/actions-admin/groups';
const API_ACTIONS = '/api/v1/actions-admin';

class ActionAdminService {
  // ==================== ДЕЙСТВИЯ ПО СЕРВИСУ ====================

  /**
   * Получить группы с действиями для конкретного service_type.
   */
  async getActions(params: ActionsTreeParams): Promise<ActionsTreeResponse> {
    const queryParams: string[] = [];
    queryParams.push(`service_type=${encodeURIComponent(params.service_type)}`);

    if (params.include_hidden) {
      queryParams.push('include_hidden=true');
    }
    if (params.include_deleted) {
      queryParams.push('include_deleted=true');
    }
    if (params.stage_code) {
      queryParams.push(`stage_code=${encodeURIComponent(params.stage_code)}`);
    }
    if (params.is_bulk !== undefined) {
      queryParams.push(`is_bulk=${params.is_bulk}`);
    }
    if (params.is_single !== undefined) {
      queryParams.push(`is_single=${params.is_single}`);
    }
    if (params.is_system !== undefined) {
      queryParams.push(`is_system=${params.is_system}`);
    }

    const queryString = `?${queryParams.join('&')}`;
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/actions${queryString}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить действия');
    }
    return await res.json();
  }

  // ==================== ГРУППЫ ====================

  /**
   * Получить список групп
   */
  async listGroups(params?: ActionGroupListParams): Promise<ActionGroup[]> {
    const queryParams: string[] = [];

    if (params?.include_deleted) {
      queryParams.push('include_deleted=true');
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const res = await authService.fetchWithAuth(API_GROUPS + queryString);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список групп');
    }
    return await res.json();
  }

  /**
   * Получить группу по ID
   */
  async getGroupById(id: number): Promise<ActionGroup> {
    const res = await authService.fetchWithAuth(`${API_GROUPS}/${id}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Группа не найдена');
    }
    return await res.json();
  }

  /**
   * Создать группу
   */
  async createGroup(payload: CreateActionGroupPayload): Promise<ActionGroup> {
    const res = await authService.fetchWithAuth(API_GROUPS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать группу');
    }
    return await res.json();
  }

  /**
   * Обновить группу
   */
  async updateGroup(id: number, payload: UpdateActionGroupPayload): Promise<ActionGroup> {
    const res = await authService.fetchWithAuth(`${API_GROUPS}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить группу');
    }
    return await res.json();
  }

  /**
   * Удалить группу
   */
  async deleteGroup(id: number): Promise<{ detail: string }> {
    const res = await authService.fetchWithAuth(`${API_GROUPS}/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить группу');
    }
    return await res.json();
  }

  /**
   * Восстановить группу
   */
  async restoreGroup(id: number): Promise<ActionGroup> {
    const res = await authService.fetchWithAuth(`${API_GROUPS}/${id}/restore`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось восстановить группу');
    }
    return await res.json();
  }

  // ==================== ДЕЙСТВИЯ ====================

  /**
   * Получить список действий
   */
  async listActions(params?: ActionListParams): Promise<Action[]> {
    const queryParams: string[] = [];

    if (params?.q && params.q.trim()) {
      queryParams.push(`q=${encodeURIComponent(params.q.trim())}`);
    }
    if (params?.service_type) {
      queryParams.push(`service_type=${encodeURIComponent(params.service_type)}`);
    }
    if (params?.service_types && params.service_types.length > 0) {
      params.service_types.forEach((st) => {
        queryParams.push(`service_types=${encodeURIComponent(st)}`);
      });
    }
    if (params?.group_code) {
      queryParams.push(`group_code=${encodeURIComponent(params.group_code)}`);
    }
    if (params?.stage_code) {
      queryParams.push(`stage_code=${encodeURIComponent(params.stage_code)}`);
    }
    if (params?.is_bulk !== undefined) {
      queryParams.push(`is_bulk=${params.is_bulk}`);
    }
    if (params?.is_single !== undefined) {
      queryParams.push(`is_single=${params.is_single}`);
    }
    if (params?.is_system !== undefined) {
      queryParams.push(`is_system=${params.is_system}`);
    }
    if (params?.include_hidden) {
      queryParams.push('include_hidden=true');
    }
    if (params?.include_deleted) {
      queryParams.push('include_deleted=true');
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const res = await authService.fetchWithAuth(API_ACTIONS + queryString);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список действий');
    }
    return await res.json();
  }

  /**
   * Получить действия, привязанные к этапу через tree API
   * Возвращает ActionsTreeResponse с группами и действиями
   */
  async getActionsForStage(
    stageCode: string,
    serviceType: string,
    includeHidden = true
  ): Promise<ActionsTreeResponse> {
    const params = new URLSearchParams({
      service_type: serviceType,
      stage_code: stageCode,
    });
    if (includeHidden) {
      params.append('include_hidden', 'true');
    }
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/actions?${params.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить действия этапа');
    }
    return await res.json();
  }

  /**
   * Получить действие по ID
   */
  async getActionById(id: number): Promise<Action> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${id}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Действие не найдено');
    }
    return await res.json();
  }

  /**
   * Получить действие по коду
   */
  async getActionByCode(code: string): Promise<Action> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/by-code/${encodeURIComponent(code)}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Действие не найдено');
    }
    return await res.json();
  }

  /**
   * Создать действие
   */
  async createAction(payload: CreateActionPayload): Promise<Action> {
    const res = await authService.fetchWithAuth(API_ACTIONS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать действие');
    }
    return await res.json();
  }

  /**
   * Обновить действие
   */
  async updateAction(id: number, payload: UpdateActionPayload): Promise<Action> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить действие');
    }
    return await res.json();
  }

  /**
   * Удалить действие
   */
  async deleteAction(id: number): Promise<{ detail: string }> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить действие');
    }
    return await res.json();
  }

  /**
   * Восстановить действие
   */
  async restoreAction(id: number): Promise<Action> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${id}/restore`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось восстановить действие');
    }
    return await res.json();
  }

  // ==================== SERVICE TYPES ====================

  /**
   * Получить service_types действия
   */
  async getActionServiceTypes(actionId: number): Promise<string[]> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${actionId}/service-types`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить service_types');
    }
    return await res.json();
  }

  /**
   * Добавить service_type к действию
   */
  async addActionServiceType(actionId: number, payload: AddServiceTypePayload): Promise<Action> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${actionId}/service-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось добавить service_type');
    }
    return await res.json();
  }

  /**
   * Удалить service_type из действия
   */
  async removeActionServiceType(actionId: number, serviceType: string): Promise<Action> {
    const res = await authService.fetchWithAuth(
      `${API_ACTIONS}/${actionId}/service-types/${encodeURIComponent(serviceType)}`,
      {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить service_type');
    }
    return await res.json();
  }

  // ==================== МАССОВЫЕ ОПЕРАЦИИ ====================

  /**
   * Массовое обновление группы
   */
  async bulkUpdateGroup(payload: BulkUpdateGroupPayload): Promise<BulkOperationResponse> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/bulk/update-group`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось выполнить массовое обновление');
    }
    return await res.json();
  }

  /**
   * Массовое обновление service_types
   */
  async bulkUpdateServiceTypes(payload: BulkUpdateServiceTypesPayload): Promise<BulkOperationResponse> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/bulk/update-service-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось выполнить массовое обновление');
    }
    return await res.json();
  }

  // ==================== ЭТАПЫ ДЕЙСТВИЙ (ACTION STAGES) ====================

  /**
   * Получить список этапов для действия
   */
  async getActionStages(actionId: number): Promise<ActionStagesResponse> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${actionId}/stages`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить этапы действия');
    }
    return await res.json();
  }

  /**
   * Добавить этап к действию
   */
  async addActionStage(actionId: number, payload: AddActionStagePayload): Promise<ActionStage> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${actionId}/stages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось добавить этап');
    }
    return await res.json();
  }

  /**
   * Обновить атрибуты этапа для действия
   */
  async updateActionStage(
    actionId: number,
    stageCode: string,
    payload: UpdateActionStagePayload
  ): Promise<ActionStage> {
    const res = await authService.fetchWithAuth(
      `${API_ACTIONS}/${actionId}/stages/${encodeURIComponent(stageCode)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить этап');
    }
    return await res.json();
  }

  /**
   * Удалить этап из действия
   */
  async removeActionStage(actionId: number, stageCode: string): Promise<{ detail: string }> {
    const res = await authService.fetchWithAuth(
      `${API_ACTIONS}/${actionId}/stages/${encodeURIComponent(stageCode)}`,
      {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить этап');
    }
    return await res.json();
  }

  /**
   * Массовая установка этапов для действия
   */
  async setActionStages(
    actionId: number,
    payload: SetActionStagesPayload
  ): Promise<SetActionStagesResponse> {
    const res = await authService.fetchWithAuth(`${API_ACTIONS}/${actionId}/stages/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось установить этапы');
    }
    return await res.json();
  }
}

export default new ActionAdminService();
